import {
  DELETE_MAPPING_FAILURE,
  DELETE_MAPPING_SUCCESS,
  FETCH_ADD_RETURN_DATA_FAILURE,
  FETCH_ADD_RETURN_DATA_POST_SUCCESS,
  FETCH_ADD_RETURN_DATA_PRE_SUCCESS,
  FETCH_ADD_RETURN_DATA_RATES_SUCCESS,
  FETCH_ADD_RETURN_DATA_REQUEST,
  RESET_RETURN_DATA,
  SAVE_MAPPING_FAILURE,
  SAVE_MAPPING_REQUEST,
  SAVE_MAPPING_SUCCESS,
  FETCH_RETURN_TYPES_REQUEST,
  FETCH_RETURN_TYPES_SUCCESS,
  FETCH_RETURN_TYPES_FAILURE,
  FETCH_DEFAULT_TAX_RATES_REQUEST,
  FETCH_DEFAULT_TAX_RATES_SUCCESS,
  FETCH_DEFAULT_TAX_RATES_FAILURE,
  SET_TAX_RATES_TO_BOXES_MAPPING,
  MAP_TAX_RATES_TO_BOXES,
  MAP_TAX_RATES_TO_BOXES_FAILURE,
  RESET_AI_MAPPING_STATUS,
} from './constants';
import {
  FetchAddReturnDataPostSuccessAction,
  FetchAddReturnDataPreSuccessAction,
  FetchAddReturnDataRatesSuccessAction,
  FetchReturnTypesSuccessAction,
  FetchDefaultTaxRatesSuccessAction,
  ReturnAction,
  ReturnState,
} from './types';

const initialState: ReturnState = {
  loading: false,
  error: undefined,
  errorCode: undefined,
  returnData: undefined,
  preLoading: false,
  taxRatesLoading: false,
  returnTypesData: undefined,
  defaultTaxRatesData: undefined,
  aiMappingStatus: 'idle',
};

export const returnReducer = (
  state = initialState,
  action: ReturnAction
): ReturnState => {
  switch (action.type) {
    case FETCH_ADD_RETURN_DATA_REQUEST:
      return handleFetchAddReturnDataRequest(state);
    case FETCH_ADD_RETURN_DATA_PRE_SUCCESS:
      return handleFetchAddReturnDataPreSuccess(state, action);
    case FETCH_ADD_RETURN_DATA_RATES_SUCCESS:
      return handleFetchAddReturnDataRatesSuccess(state, action);
    case FETCH_ADD_RETURN_DATA_POST_SUCCESS:
      return handleFetchAddReturnDataPostSuccess(state, action);
    case FETCH_ADD_RETURN_DATA_FAILURE:
      return handleFetchAddReturnDataFailure(state);
    case FETCH_RETURN_TYPES_REQUEST:
      return handleFetchReturnTypesRequest(state);
    case FETCH_RETURN_TYPES_SUCCESS:
      return handleFetchReturnTypesSuccess(state, action);
    case FETCH_RETURN_TYPES_FAILURE:
      return handleFetchReturnTypesFailure(state);
    case FETCH_DEFAULT_TAX_RATES_REQUEST:
      return handleFetchDefaultTaxRatesRequest(state);
    case FETCH_DEFAULT_TAX_RATES_SUCCESS:
      return handleFetchDefaultTaxRatesSuccess(state, action);
    case FETCH_DEFAULT_TAX_RATES_FAILURE:
      return handleFetchDefaultTaxRatesFailure(state);
    case SAVE_MAPPING_REQUEST:
    case SAVE_MAPPING_FAILURE:
      return handleRequest(state);
    case SAVE_MAPPING_SUCCESS:
    case DELETE_MAPPING_SUCCESS:
      return handleSuccess(state);
    case DELETE_MAPPING_FAILURE:
    case SAVE_MAPPING_FAILURE:
      return handleFailure(state);
    case RESET_RETURN_DATA:
      return handleResetReturnData();
    case SET_TAX_RATES_TO_BOXES_MAPPING:
      return handleSetTaxRatesToBoxesMapping(state, action);
    case MAP_TAX_RATES_TO_BOXES:
      return handleMapTaxRatesToBoxes(state);
    case MAP_TAX_RATES_TO_BOXES_FAILURE:
      return handleMapTaxRatesToBoxesFailure(state);
    case RESET_AI_MAPPING_STATUS:
      return handleResetAiMappingStatus(state);
    default:
      return state;
  }
};

// Fetch returns
function handleFetchAddReturnDataRequest(state: ReturnState): ReturnState {
  return {
    ...state,
    loading: true,
    preLoading: true,
  };
}

function handleFetchAddReturnDataPreSuccess(
  state: ReturnState,
  action: FetchAddReturnDataPreSuccessAction
): ReturnState {
  const { returnTypes, returnTypesEdit, returnFrequency, connectionId } =
    action.payload;

  const returnData = {
    ...state.returnData,
    returnTypes,
    returnTypesEdit,
    returnFrequency,
    connectionId,
  };
  return {
    ...state,
    preLoading: false,
    taxRatesLoading: true,
    returnData,
  };
}

function handleFetchAddReturnDataRatesSuccess(
  state: ReturnState,
  action: FetchAddReturnDataRatesSuccessAction
): ReturnState {
  const { taxRates } = action.payload;
  const returnData = { ...state.returnData, taxRates };
  return {
    ...state,
    taxRatesLoading: false,
    returnData,
  };
}

function handleFetchAddReturnDataPostSuccess(
  state: ReturnState,
  action: FetchAddReturnDataPostSuccessAction
): ReturnState {
  const { sections, sectionsReturnTypeId } = action.payload;
  const secs = [...sections];
  secs.forEach((s) => {
    s.subsections?.sort((a, b) => a.sortOrder - b.sortOrder);
  });
  const returnData = { ...state.returnData, sections, sectionsReturnTypeId };
  return {
    ...state,
    loading: false,
    returnData,
  };
}

function handleFetchAddReturnDataFailure(state: ReturnState): ReturnState {
  return {
    ...state,
    loading: false,
    taxRatesLoading: false,
    preLoading: false,
  };
}

// Fetch Return Types (new standalone flow)
function handleFetchReturnTypesRequest(state: ReturnState): ReturnState {
  return {
    ...state,
    loading: true,
  };
}

function handleFetchReturnTypesSuccess(
  state: ReturnState,
  action: FetchReturnTypesSuccessAction
): ReturnState {
  const { returnTypesData } = action.payload;
  return {
    ...state,
    loading: false,
    returnTypesData,
  };
}

function handleFetchReturnTypesFailure(state: ReturnState): ReturnState {
  return {
    ...state,
    loading: false,
    returnTypesData: undefined,
  };
}

// Fetch Default Tax Rates (new standalone flow)
function handleFetchDefaultTaxRatesRequest(state: ReturnState): ReturnState {
  return {
    ...state,
    loading: true,
  };
}

function handleFetchDefaultTaxRatesSuccess(
  state: ReturnState,
  action: FetchDefaultTaxRatesSuccessAction
): ReturnState {
  const { defaultTaxRatesData } = action.payload;
  return {
    ...state,
    loading: false,
    defaultTaxRatesData,
  };
}

function handleFetchDefaultTaxRatesFailure(state: ReturnState): ReturnState {
  return {
    ...state,
    loading: false,
    defaultTaxRatesData: undefined,
  };
}

// Common
function handleRequest(state: ReturnState): ReturnState {
  return {
    ...state,
    loading: true,
  };
}

function handleSuccess(state: ReturnState): ReturnState {
  return {
    ...state,
    loading: false,
  };
}

function handleFailure(state: ReturnState): ReturnState {
  return {
    ...state,
    loading: false,
  };
}

// Reset
function handleResetReturnData(): ReturnState {
  return initialState;
}

function handleSetTaxRatesToBoxesMapping(
  state: ReturnState,
  action: any
): ReturnState {
  return {
    ...state,
    taxRatesToBoxesMapping: action.payload.mapping,
    aiMappingStatus: 'success',
  };
}

function handleMapTaxRatesToBoxes(state: ReturnState): ReturnState {
  return {
    ...state,
    aiMappingStatus: 'loading',
  };
}

function handleMapTaxRatesToBoxesFailure(state: ReturnState): ReturnState {
  return {
    ...state,
    aiMappingStatus: 'error',
  };
}

function handleResetAiMappingStatus(state: ReturnState): ReturnState {
  return {
    ...state,
    taxRatesToBoxesMapping: undefined,
    aiMappingStatus: 'idle',
  };
}